import React, { useState } from "react";
import { graphql, navigate } from "gatsby";
import { GatsbyImage, getImage, ImageDataLike } from "gatsby-plugin-image";

import MainLayout from "../../layouts/main-layout";
import { ISite } from "../../models/site.model";
import { IPageBase } from "../../models/page-base.model";
import { IPostBrief } from "../../models/post.model";
import { ISliderProduct } from "../../models/slider-product.model";
import QueryEdgeNode from "../../models/query-edge-node.model";
import usePageMetadata from "../../hooks/use-page-metadata";
import useIntersectionObserver from "../../hooks/useIntersectionObserver";
import getNodes from "../../utils/get-nodes";
import { getMediaWithRelation } from "../../utils/get-relation";
import { config } from "../../config";
import useUser from "../../apps/auth/hooks/use-user";
import { getAbsolutePath } from "../../apps/utility/routes.utility";
import Link from "../atoms/link";
import ProductsSlider from "../organisms/sections/home/products-slider";
import MinimalFooter from "../organisms/minimal-footer";
import clockIcon from "../../assets/icons/clock.svg";
import checkIcon from "../../assets/icons/check.svg";
import favoriteIcon from "../../assets/icons/favorite.svg";
import hangerIcon from "../../assets/icons/hanger.svg";
import magicWandIcon from "../../assets/icons/magic-wand.svg";
import mannequinIcon from "../../assets/icons/mannequin.svg";
import questionnaireIcon from "../../assets/icons/questionnaire.svg";
import shoppingBagIcon from "../../assets/icons/shopping-bag.svg";
import starIcon from "../../assets/icons/star.svg";
import logoButterfly from "../../assets/images/logo_butterfly.png";
import logoFull from "../../assets/images/logo_full.png";
import heroStyleNote from "../../assets/images/Twój_Styl_Twoja_Magia.png";
import homeBeautyImage from "../../assets/images/home/beauty.jpg";
import colourIcon from "../../assets/images/home/colour.svg";
import homeFaceOne from "../../assets/images/home/buzie_1.png";
import homeFaceTwo from "../../assets/images/home/buzie_2.png";
import homeFaceThree from "../../assets/images/home/buzie_3.png";
import heightIcon from "../../assets/images/home/height.svg";
import homeMeasureImage from "../../assets/images/home/measure.jpg";
import homeWhatFitImage from "../../assets/images/home/what-fit_croped.jpg";
import womenIcon from "../../assets/images/home/women.svg";
import * as styles from "./index.module.scss";

interface IIndexProps extends IPageBase {
    readonly data: {
        site: ISite;
        welcomePhotoLandscape: ImageDataLike;
        welcomePhotoMobile: ImageDataLike;
        latestPosts: QueryEdgeNode<IPostBrief>;
        sliderProducts: QueryEdgeNode<ISliderProduct>;
    };
}

const navItems = [
    { label: "O Mnie", to: "/o-nas/" },
    { label: "Blog", to: "/blog/" },
    { label: "Kontakt", to: "/kontakt/" },
];

const benefitItems = [
    {
        icon: favoriteIcon,
        title: "Dopasowane do Ciebie",
        text: "Twój styl, Twoje potrzeby, Twoje zasady.",
    },
    {
        icon: shoppingBagIcon,
        title: "Wiele sklepów w jednym miejscu",
        text: "Setki produktów, zero przeszukiwania.",
    },
    {
        icon: clockIcon,
        title: "Oszczędność czasu i pieniędzy",
        text: "Gotowe stylizacje w kilka minut.",
    },
    {
        icon: starIcon,
        title: "Inspiracje na co dzień",
        text: "Trendy, porady i stylizacje na każdą okazję.",
    },
];

const processItems = [
    {
        icon: questionnaireIcon,
        title: "Wypełnij quiz stylu",
        text: "Poznajemy Twój gust, potrzeby i sylwetkę.",
    },
    {
        icon: mannequinIcon,
        title: "Wybierz swoje opcje",
        text: "Okazja, budżet, styl i ulubione kolory.",
    },
    {
        icon: magicWandIcon,
        title: "Odbierz stylizacje",
        text: "Gotowe propozycje z wielu sklepów w jednym miejscu.",
    },
    {
        icon: hangerIcon,
        title: "Zapisz i przymierz",
        text: "Wracaj do ulubionych zestawów i zobacz je na sobie.",
    },
];

const styleCards = [
    {
        image: homeWhatFitImage,
        icon: womenIcon,
        title: "Określ swoją figurę",
        text: "Dowiedz się, co Ci pasuje",
        to: "/okresl-figure/",
    },
    {
        image: homeBeautyImage,
        icon: colourIcon,
        title: "Znajdź swój kolor",
        text: "Odkryj barwy, które Cię podkreślają",
        to: "/typ-urody/",
    },
    {
        image: homeMeasureImage,
        icon: heightIcon,
        title: "Jak dobrze się zmierzyć",
        text: "Kupuj online bez pomyłek",
        to: "/jak-prawidlowo-sie-mierzyc/",
    },
];

const IndexPage: React.FC<IIndexProps> = ({ data, pageContext }) => {
    const { defaultStructuredData } = usePageMetadata(pageContext, data.site);
    const heroImage = getImage(data.welcomePhotoLandscape);
    const heroMobileImage = getImage(data.welcomePhotoMobile);
    const latestPosts = getNodes(data.latestPosts);
    const sliderProducts = getNodes(data.sliderProducts);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { isLoggedIn } = useUser();
    const accountLinkHref = isLoggedIn ? getAbsolutePath("ACCOUNT_DASHBOARD") : "/auth/";
    const accountLinkLabel = isLoggedIn ? "Twoje konto" : "Zaloguj się";
    const [styleSectionRef, styleSectionEntry] = useIntersectionObserver<HTMLElement>({
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.08,
    });
    const [blogSectionRef, blogSectionEntry] = useIntersectionObserver<HTMLElement>({
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.08,
    });

    return (
        <MainLayout
            HeaderComponent={null}
            footer={false}
            className={styles.landingPage}
            SEOProps={{ structuredData: defaultStructuredData }}
        >
            <section className={styles.hero}>
                {heroImage && (
                    <div className={styles.heroMedia} aria-hidden="true">
                        <GatsbyImage
                            image={heroImage}
                            alt=""
                            className={`${styles.heroImage} ${styles.heroImageDesktop}`}
                        />
                        {heroMobileImage && (
                            <GatsbyImage
                                image={heroMobileImage}
                                alt=""
                                className={`${styles.heroImage} ${styles.heroImageMobile}`}
                            />
                        )}
                    </div>
                )}
                <div className={styles.heroOverlay} />

                <nav className={styles.heroNav} aria-label="Główna nawigacja">
                    <Link to="/" className={styles.brand}>
                        <img
                            src={logoFull}
                            alt="Fashion Fairy Virtual Stylist"
                            className={styles.brandLogo}
                        />
                    </Link>
                    <div className={styles.navLinks}>
                        {navItems.map(item => (
                            <Link key={item.to} to={item.to}>
                                {item.label}
                            </Link>
                        ))}
                    </div>
                    <Link to={accountLinkHref} className={styles.loginButton} noPrefetch>
                        {accountLinkLabel}
                    </Link>
                    <button
                        className={styles.menuToggle}
                        type="button"
                        aria-label={isMenuOpen ? "Zamknij menu" : "Otwórz menu"}
                        aria-expanded={isMenuOpen}
                        aria-controls="home-mobile-menu"
                        onClick={() => setIsMenuOpen(isOpen => !isOpen)}
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                    <div
                        id="home-mobile-menu"
                        className={`${styles.mobileMenu} ${
                            isMenuOpen ? styles.mobileMenuOpen : ""
                        }`}
                    >
                        {navItems.map(item => (
                            <Link key={item.to} to={item.to}>
                                {item.label}
                            </Link>
                        ))}
                        <button
                            type="button"
                            onClick={() => {
                                setIsMenuOpen(false);
                                navigate(accountLinkHref);
                            }}
                        >
                            {accountLinkLabel}
                        </button>
                    </div>
                </nav>

                <div className={styles.heroContent}>
                    <p className={styles.kicker}>Twój styl. Twoje zasady.</p>
                    <h1>
                        Wystylizuj się <span>idealnie.</span>
                        <br />
                        Bez szukania.
                    </h1>
                    <p className={styles.heroText}>
                        Stylizacje dopasowane do Ciebie, Twojego stylu i budżetu.
                    </p>
                    <Link to="/wystylizuj-mnie/" className={styles.primaryButton}>
                        <img src={magicWandIcon} alt="" aria-hidden="true" />
                        <span>Wystylizuj mnie</span>
                    </Link>
                    <div className={styles.heroProof}>
                        <div className={styles.avatarStack} aria-hidden="true">
                            <img src={homeFaceOne} alt="" />
                            <img src={homeFaceTwo} alt="" />
                            <img src={homeFaceThree} alt="" />
                        </div>
                        <p>
                            <strong>10 000+ stylizacji</strong>
                            <span>wygenerowanych w tym tygodniu</span>
                        </p>
                    </div>
                    <div className={styles.quickFacts}>
                        {["Szybko", "Prosto", "Dla Ciebie"].map(item => (
                            <span key={item}>
                                <img src={checkIcon} alt="" aria-hidden="true" />
                                {item}
                            </span>
                        ))}
                    </div>
                </div>

                <aside className={styles.heroNote} aria-label="Twój styl. Twoja magia. My zajmiemy się resztą.">
                    <img
                        src={heroStyleNote}
                        alt="Twój styl. Twoja magia. My zajmiemy się resztą."
                    />
                </aside>
            </section>

            <section className={styles.benefits} aria-label="Korzyści">
                {benefitItems.map(item => (
                    <article key={item.title} className={styles.benefitItem}>
                        <span aria-hidden="true">
                            <img src={item.icon} alt="" />
                        </span>
                        <div>
                            <h2>{item.title}</h2>
                            <p>{item.text}</p>
                        </div>
                    </article>
                ))}
            </section>

            <section className={styles.processSection} aria-labelledby="process-title">
                <h2 id="process-title">Zobacz, jakie to proste!</h2>
                <div className={styles.processGrid}>
                    {processItems.map((item, index) => (
                        <article key={item.title} className={styles.processItem}>
                            <div className={styles.processIconWrap}>
                                <img src={item.icon} alt="" aria-hidden="true" />
                            </div>
                            <span className={styles.processNumber}>{index + 1}</span>
                            <h3>{item.title}</h3>
                            <p>{item.text}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className={styles.styleSection} ref={styleSectionRef}>
                <p className={styles.sectionEyebrow}>Odkryj swoją modową stronę</p>
                <h2>Wybierz i poznaj swój styl</h2>
                <div className={styles.styleGrid}>
                    {styleCards.map(card => (
                        <Link
                            key={card.title}
                            to={card.to}
                            className={`${styles.styleCard} ${styles.revealCard} ${
                                styleSectionEntry?.isIntersecting ? styles.revealCardVisible : ""
                            }`}
                        >
                            <img
                                src={card.image}
                                alt=""
                                className={styles.cardImage}
                                aria-hidden="true"
                            />
                            <span className={styles.cardIcon} aria-hidden="true">
                                <img src={card.icon} alt="" className={styles.cardIconImage} />
                            </span>
                            <h3>{card.title}</h3>
                            <p>{card.text}</p>
                            <span className={styles.cardButton}>Sprawdź →</span>
                        </Link>
                    ))}
                </div>
            </section>

            {sliderProducts.length > 0 && <ProductsSlider products={sliderProducts} />}

            <section className={styles.inspirationSection} ref={blogSectionRef}>
                <p className={styles.sectionEyebrow}>Codzienna dawka inspiracji</p>
                <h2>Inspiracje Fashion Fairy</h2>
                <div className={styles.inspirationGrid}>
                    {latestPosts.map(post => {
                        const postImageMedia = getMediaWithRelation(
                            post.media,
                            config.relations.coverImage
                        );
                        const postImage =
                            postImageMedia?.remoteImage && getImage(postImageMedia.remoteImage);
                        const postImageUrl = postImageMedia?.url;
                        const title = post.translations?.pl?.title || post.slug;
                        const category = post.categories?.[0]?.name || "Blog";

                        return (
                            <Link
                                key={post.articleId}
                                to={`/${post.slug}/`}
                                className={`${styles.inspirationCard} ${styles.revealCard} ${
                                    blogSectionEntry?.isIntersecting ? styles.revealCardVisible : ""
                                }`}
                            >
                                <div className={styles.inspirationImageWrap}>
                                    {postImage ? (
                                        <GatsbyImage
                                            image={postImage}
                                            alt={postImageMedia?.alt || ""}
                                            className={styles.inspirationImage}
                                        />
                                    ) : postImageUrl ? (
                                        <img
                                            src={postImageUrl}
                                            alt={postImageMedia?.alt || title}
                                            className={styles.inspirationImage}
                                            loading="lazy"
                                        />
                                    ) : null}
                                    <span>{category}</span>
                                </div>
                                <h3>{title}</h3>
                                <span className={styles.readMore}>Czytaj więcej →</span>
                            </Link>
                        );
                    })}
                </div>
            </section>

            <div className={styles.footerBrandMark} aria-hidden="true">
                <img src={logoButterfly} alt="" />
            </div>
            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }

        welcomePhotoLandscape: file(relativePath: { eq: "home-hero-new.jpeg" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED, quality: 95, layout: FULL_WIDTH)
            }
        }

        welcomePhotoMobile: file(relativePath: { eq: "home-hero-mobile-v2.png" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED, quality: 92, layout: FULL_WIDTH)
            }
        }

        latestPosts: allPost(limit: 3, sort: { fields: publishedAt, order: DESC }) {
            edges {
                node {
                    ...postBriefFields
                }
            }
        }

        sliderProducts: allSliderProduct {
            edges {
                node {
                    productId
                    category
                    brandName
                    url
                    affiliateUrl
                    imgUrl
                }
            }
        }

        site {
            ...siteFields
        }
    }
`;

export default IndexPage;
